import {Tooltip} from 'platform/components';
import {HStack, Text, VStack} from 'platform/foundation';
import {useFormatCurrency} from 'platform/locale';

import {isNil} from 'ramda';

import {useGetTenantQuery} from '@dms/api/core';
import i18n from '@dms/i18n';
import {DEFAULT_CURRENCY, EMPTY_PLACEHOLDER} from '@dms/shared';

import {RequiredTestIdProps, suffixTestId} from 'shared/web';

type Price = {
  withoutVat?: {
    amount?: number | null;
    currency?: string | null;
  } | null;
  vat?: {
    amount?: number | null;
    currency?: string | null;
  } | null;
  withVat?: {
    amount?: number | null;
    currency?: string | null;
  } | null;
};

interface PriceDetailTooltipProps extends RequiredTestIdProps {
  priceType?: 'withVat' | 'withoutVat';
  materialPrice?: Price | null;
  labourPrice?: Price | null;
  smallUnitMaterialPrice?: Price | null;
  children: React.ReactNode;
}

export function PriceDetailTooltip(props: PriceDetailTooltipProps) {
  const {data: {currency: defaultCurrency} = {}} = useGetTenantQuery();
  const formatCurrency = useFormatCurrency();
  const priceType = props.priceType ?? 'withVat';
  const labourPrice = props.labourPrice?.[priceType];
  const materialPrice = props.materialPrice?.[priceType];
  const smallUnitMaterialPrice = props.smallUnitMaterialPrice?.[priceType];

  return (
    <Tooltip
      label={
        <VStack>
          <HStack spacing={1}>
            <Text size="xSmall" alternative color="white">
              {i18n.t('entity.order.priceDetail.labourPrice')}:
            </Text>
            <Text size="xSmall" alternative color="white">
              {isNil(labourPrice?.amount)
                ? EMPTY_PLACEHOLDER
                : formatCurrency(
                    labourPrice.amount,
                    labourPrice.currency ?? defaultCurrency ?? DEFAULT_CURRENCY,
                    2
                  )}
            </Text>
          </HStack>
          <HStack spacing={1}>
            <Text size="xSmall" alternative color="white">
              {i18n.t('entity.order.priceDetail.materialPrice')}:
            </Text>
            <Text size="xSmall" alternative color="white">
              {isNil(materialPrice?.amount)
                ? EMPTY_PLACEHOLDER
                : formatCurrency(
                    materialPrice.amount,
                    materialPrice.currency ?? defaultCurrency ?? DEFAULT_CURRENCY,
                    2
                  )}
            </Text>
          </HStack>
          <HStack spacing={1}>
            <Text size="xSmall" alternative color="white">
              {i18n.t('entity.order.priceDetail.smallUnitMaterialPrice')}:
            </Text>
            <Text size="xSmall" alternative color="white">
              {isNil(smallUnitMaterialPrice?.amount)
                ? EMPTY_PLACEHOLDER
                : formatCurrency(
                    smallUnitMaterialPrice.amount,
                    smallUnitMaterialPrice.currency ?? defaultCurrency ?? DEFAULT_CURRENCY,
                    2
                  )}
            </Text>
          </HStack>
        </VStack>
      }
      data-testid={suffixTestId('priceDetail', props)}
    >
      {props.children}
    </Tooltip>
  );
}
